import { useEffect, useMemo, useState } from "react";
import { Alert, Box, MenuItem, Select } from "@mui/material";
import { useTranslation } from "react-i18next";
import EmptyState from "../components/EmptyState";
import PageHeader from "../components/PageHeader";
import SectionHeader from "../components/SectionHeader";
import KpiFigure from "../components/metrics/KpiFigure";
import MetricStrip from "../components/metrics/MetricStrip";
import { PilotLockedSection } from "../components/pilot/PilotLockedView";
import { ZERO_ICONS } from "../constants/zeroIcons";
import { useNumberFormat } from "../hooks/useNumberFormat";
import { getPilotDashboardStatistics } from "../services/dashboard.service";
import { useOrganizationStore } from "../stores/organization.store";
import type { PilotDashboardStatistics } from "../types/dashboard.type";
import type { MetricValueKind } from "../utils/numberFormat";
import { formatMonth } from "../utils/numberFormat";
import {
  buildDashboardMonthOptions,
  getCurrentDashboardMonthValue,
} from "../utils/dashboardMonth.helpers";

type PilotMetric = {
  key: keyof PilotDashboardStatistics;
  titleKey: string;
  kind: MetricValueKind;
  icon: typeof ZERO_ICONS.collections;
};

const PILOT_METRICS: PilotMetric[] = [
  { key: "total_orders", titleKey: "pilot.metrics.totalOrders", kind: "count", icon: ZERO_ICONS.collections },
  { key: "completion_rate", titleKey: "pilot.metrics.completionRate", kind: "percent", icon: ZERO_ICONS.completed },
  { key: "waste_diversion_rate", titleKey: "pilot.metrics.wasteDiversionRate", kind: "percent", icon: ZERO_ICONS.rate },
  { key: "total_revenue", titleKey: "pilot.metrics.totalRevenue", kind: "sar", icon: ZERO_ICONS.revenue },
];

/** `yyyy-MM-…` month value → first day in the local calendar. */
function monthDate(value: string): Date {
  const match = /^(\d{4})-(\d{2})/.exec(value);
  return match ? new Date(Number(match[1]), Number(match[2]) - 1, 1) : new Date();
}

export default function PilotDashboard() {
  const { t, i18n } = useTranslation();
  const fmt = useNumberFormat();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentDashboardMonthValue);
  const [statistics, setStatistics] = useState<PilotDashboardStatistics | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const availableMonths = useMemo(() => buildDashboardMonthOptions(), []);

  useEffect(() => {
    if (!selectedOrganization?.id) {
      setStatistics(null);
      return;
    }

    let active = true;
    setIsLoading(true);
    setError(null);
    void getPilotDashboardStatistics(selectedOrganization.id, selectedMonth)
      .then((result) => {
        if (active) setStatistics(result);
      })
      .catch((requestError: unknown) => {
        if (!active) return;
        setStatistics(null);
        setError(
          requestError instanceof Error
            ? requestError.message
            : t("pilot.dashboardLoadError"),
        );
      })
      .finally(() => {
        if (active) setIsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [selectedOrganization?.id, selectedMonth, t]);

  return (
    <Box>
      <PageHeader
        icon={<ZERO_ICONS.dashboard />}
        title={t("dashboard.title")}
        subtitle={selectedOrganization?.name ?? t("dashboard.selectOrganization")}
        actions={
          <Select
            size="small"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
            sx={{ minWidth: 200 }}
            aria-label={t("pilot.month")}
            startAdornment={<ZERO_ICONS.period sx={{ fontSize: 18, color: "text.secondary", marginInlineEnd: 1 }} />}
          >
            {availableMonths.map((month) => (
              <MenuItem key={month.value} value={month.value}>
                {formatMonth(monthDate(month.value), i18n.language)}
              </MenuItem>
            ))}
          </Select>
        }
      />

      {!selectedOrganization ? (
        <EmptyState icon={ZERO_ICONS.dashboard} title={t("dashboard.selectOrganization")} />
      ) : null}
      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      {selectedOrganization ? (
        <Box component="section" sx={{ mb: { xs: 4, md: 5 } }}>
          <SectionHeader
            title={t("dashboard.lead.title", { period: formatMonth(monthDate(selectedMonth), i18n.language) })}
            icon={ZERO_ICONS.collections}
          />
          <MetricStrip columns={{ xs: 2, lg: 4 }}>
            {PILOT_METRICS.map((metric) => {
              const raw = statistics?.[metric.key];
              const value = typeof raw === "number" && Number.isFinite(raw) ? raw : null;
              const figure = fmt.metric(metric.kind, value);
              return (
                <KpiFigure
                  key={metric.key}
                  icon={metric.icon}
                  label={t(metric.titleKey)}
                  value={figure.text}
                  unit={figure.empty ? undefined : figure.unit || undefined}
                  size={metric.key === "total_orders" ? "hero" : "lg"}
                  loading={isLoading}
                />
              );
            })}
          </MetricStrip>
        </Box>
      ) : null}

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2.5 }}>
        <PilotLockedSection minHeight={320} label={t("dashboard.materials.title")} preview="chart" />
        <PilotLockedSection minHeight={320} label={t("pilot.chartsAndMap")} preview="map" />
      </Box>
    </Box>
  );
}
